import { all, call, put, takeEvery } from 'redux-saga/effects';
import { fetchEntity } from '@classpass/saga-tools';
import { reportError } from 'helpers/error-logging';
import { userSettings, userSmsSubscriptionStatus as userSmsSubscriptionStatusEntity } from 'store/saga/entities/bound';
import { createErrorSnackbar } from 'components/Snackbar';
import { createInfoBanner } from 'components/Banner';
import snakeCase from 'lodash/snakeCase';
import { parseNumber } from 'helpers/core';
import { NOTIFICATION_TYPE } from './Notifications.constants';
import messages from './Notifications.messages';

export const UPDATE_NOTIFICATIONS = 'venuePortal/notifications/UPDATE_NOTIFICATIONS';
export const updateNotifications = (formValues, asyncFns) => ({ type: UPDATE_NOTIFICATIONS, formValues, asyncFns });

const getApiErrorDetail = error => {
  const data = error.response?.data;
  if (typeof data === 'string') return data || null;
  if (typeof data?.message === 'string' && data.message) return data.message;
  if (typeof data?.title === 'string' && data.title) return data.title;
  if (Array.isArray(data?.errors)) {
    const joined = data.errors
      .map(item => (typeof item === 'string' ? item : item?.message))
      .filter(Boolean)
      .join(' ');
    if (joined) return joined;
  }
  if (typeof data?.error === 'string' && data.error) return data.error;
  if (typeof data?.error?.message === 'string' && data.error.message) return data.error.message;
  return null;
};

export function* doUpdateNotifications(client, action) {
  const {
    formValues: {
      userId,
      applicableVenueIds,
      currentReceiveNotifications,
      currentUserSmsSubscriptionStatus,
      currentUserPhoneNumber,
      receiveNotifications,
      userSmsSubscriptionStatus,
      userPhoneNumber,
    },
    asyncFns: { resolveSubmit, rejectSubmit },
  } = action;
  // Write explicit rows for every applicable venue, not just changed ones — venues without a
  // row fall back to the backend default (opt-in), so a save must self-heal that gap (ST-8959).
  const settings = Object.entries(applicableVenueIds).reduce((updates, [type, venueIds]) => {
    const enabledIds = currentReceiveNotifications[type] || [];
    venueIds.forEach(venueId => {
      updates.push({
        userId,
        venueId: parseNumber(venueId),
        settingName: snakeCase(type),
        settingValue: enabledIds.includes(venueId) ? 'yes' : 'no',
      });
    });
    return updates;
  }, []);

  // SMS stays diff-only: its single-select dropdown renders value[0], so a full write could
  // persist venue rows the user never saw.
  const smsType = NOTIFICATION_TYPE.CAN_RECEIVE_SMS;
  const currentSmsIds = currentReceiveNotifications[smsType] || [];
  const initialSmsIds = receiveNotifications[smsType] || [];
  currentSmsIds
    .filter(venueId => !initialSmsIds.includes(venueId))
    .forEach(venueId => {
      settings.push({
        userId,
        venueId: parseNumber(venueId),
        settingName: snakeCase(smsType),
        settingValue: 'yes',
      });
    });
  initialSmsIds
    .filter(venueId => !currentSmsIds.includes(venueId))
    .forEach(venueId => {
      settings.push({
        userId,
        venueId: parseNumber(venueId),
        settingName: snakeCase(smsType),
        settingValue: 'no',
      });
    });

  if (currentUserSmsSubscriptionStatus !== userSmsSubscriptionStatus || currentUserPhoneNumber !== userPhoneNumber) {
    // If user tries to delete phone number from input, disable, and save, we default to the initial phone value.
    // This prevents saving an empty string to the database and allows user to opt back in without re-entering phone number.
    // Check that phone number is not falsy to prevent submitting undefined number.
    if (!!currentUserPhoneNumber || !!userPhoneNumber) {
      const phone = currentUserPhoneNumber || userPhoneNumber;
      const countryCode = phone.substring(0, 2);
      const phoneNumber = phone.substring(2);
      const smsSettings = {
        countryCode,
        phoneNumber,
        optIn: currentUserSmsSubscriptionStatus,
      };
      try {
        yield call(fetchEntity, userSmsSubscriptionStatusEntity, [client, { smsSettings, userId }]);
      } catch (error) {
        const detail = getApiErrorDetail(error);
        yield all([
          put(createErrorSnackbar(detail || messages.updateError, {}, { skipIntl: !!detail })),
          detail ? call(rejectSubmit, null, { userPhoneNumber: detail }) : call(rejectSubmit),
          call(reportError, error),
        ]);
        return;
      }
    }
  }

  try {
    yield call(fetchEntity, userSettings, [client, { settings, userId }]);
  } catch (error) {
    const detail = getApiErrorDetail(error);
    yield all([
      put(createErrorSnackbar(detail || messages.updateError, {}, { skipIntl: !!detail })),
      call(rejectSubmit),
      call(reportError, error),
    ]);
    return;
  }

  yield put(createInfoBanner(messages.updateSuccess));
  yield call(resolveSubmit);
}

export function* handleRouteEnter(client) {
  try {
    yield all([
      call(fetchEntity, userSettings, [client]),
      call(fetchEntity, userSmsSubscriptionStatusEntity, [client]),
    ]);
  } catch (error) {
    yield all([put(createErrorSnackbar(messages.loadError)), call(reportError, error)]);
  }
}

export function* watchActions(client) {
  yield takeEvery(UPDATE_NOTIFICATIONS, doUpdateNotifications, client);
}
